import { useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Download,
  Eye,
  Mail,
  RefreshCw,
  Search,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";
import { canResendEmailLog } from "@/lib/emailLogResendPolicy";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SideDrawer } from "@/components/common/SideDrawer";
import { EmailLoggingService } from "@/services/communication/EmailLoggingService";
import type { EmailFilters, EmailLog } from "@/types/email-logs";
import {
  useEmailLogs,
  useEmailStats,
  useResendEmail,
} from "./hooks/useEmailLogs";

const EmailLogsPage = () => {
  const [filters, setFilters] = useState<EmailFilters>({});
  const [page, setPage] = useState(1);
  const [selectedEmail, setSelectedEmail] = useState<EmailLog | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"preview" | "html" | "text">(
    "preview",
  );

  const { data: emailData, isLoading } = useEmailLogs(filters, page, 50);
  const { data: stats } = useEmailStats(filters);
  const resendMutation = useResendEmail();

  const handleExport = async () => {
    try {
      const csvContent = await EmailLoggingService.exportToCSV(filters);
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `email-logs-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      toast.success("Email logs exported");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Export failed: ${message}`);
    }
  };

  const handleViewDetails = (email: EmailLog) => {
    setSelectedEmail(email);
    setIsDetailOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { icon: LucideIcon; className: string }> = {
      sent: {
        icon: CheckCircle,
        className:
          "bg-genie-primary-light text-genie-primary border-genie-primary/20",
      },
      failed: {
        icon: XCircle,
        className:
          "bg-genie-error-light text-genie-error border-genie-error/20",
      },
      pending: {
        icon: Clock,
        className:
          "bg-genie-background-muted text-genie-text-muted border-genie-border",
      },
      retrying: {
        icon: RefreshCw,
        className:
          "bg-genie-accent-light text-genie-accent-dark border-genie-accent/20",
      },
    };

    const variant = variants[status] || variants.pending;
    const Icon = variant.icon;

    return (
      <Badge
        className={cn(variant.className, "flex items-center gap-1 border")}
      >
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const getEmailTypeColor = (type: string) => {
    // Use consistent brand color for all email types
    return "bg-genie-primary-light text-genie-primary border border-genie-primary/20";
  };

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1920px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Mail className="h-8 w-8 text-genie-secondary" />
            <div>
              <h1 className="text-3xl font-bold text-genie-text-primary">
                Email Logs
              </h1>
              <p className="text-genie-text-secondary">
                Monitor and audit all email communications
              </p>
            </div>
          </div>
          <Button onClick={handleExport} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">
                    Total Emails
                  </p>
                  <p className="text-2xl font-bold text-genie-text-primary">
                    {stats.total}
                  </p>
                </div>
                <Mail className="h-8 w-8 text-genie-primary" />
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Sent</p>
                  <p className="text-2xl font-bold text-genie-primary">
                    {stats.sent}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-genie-primary" />
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Failed</p>
                  <p className="text-2xl font-bold text-genie-error">
                    {stats.failed}
                  </p>
                </div>
                <XCircle className="h-8 w-8 text-genie-error" />
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">
                    Success Rate
                  </p>
                  <p className="text-2xl font-bold text-genie-primary">
                    {stats.successRate}%
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-genie-primary" />
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg p-4 mb-6 border border-genie-border">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-genie-text-muted" />
              <Input
                placeholder="Search subject or recipient..."
                className="pl-10"
                value={filters.search || ""}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
              />
            </div>
            <Select
              value={filters.email_type || "all"}
              onValueChange={(value) =>
                setFilters({
                  ...filters,
                  email_type: value === "all" ? undefined : value,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Email Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="welcome_user_created">
                  Welcome Email
                </SelectItem>
                <SelectItem value="conversation_report">
                  Conversation Report
                </SelectItem>
                <SelectItem value="notification">Notification</SelectItem>
                <SelectItem value="test">Test</SelectItem>
                <SelectItem value="connection_test">Connection Test</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.status || "all"}
              onValueChange={(value) =>
                setFilters({
                  ...filters,
                  status: value === "all" ? undefined : value,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="retrying">Retrying</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => setFilters({})}
              className="w-full"
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Email Table */}
        <div className="bg-white rounded-lg border border-genie-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Recipients</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-8 text-genie-text-secondary"
                  >
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                    Loading email logs...
                  </TableCell>
                </TableRow>
              ) : emailData?.data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-8 text-genie-text-muted"
                  >
                    No email logs found
                  </TableCell>
                </TableRow>
              ) : (
                emailData?.data.map((email) => (
                  <TableRow key={email.id}>
                    <TableCell>
                      <Badge className={getEmailTypeColor(email.email_type)}>
                        {email.email_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-genie-text-primary">
                      {email.subject}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-genie-text-secondary">
                        {email.recipients.slice(0, 2).map((recipient, index) => (
                          <CopyableEmail key={`${recipient}-${index}`} email={recipient} className="mr-2" />
                        ))}
                        {email.recipients.length > 2 &&
                          ` +${email.recipients.length - 2}`}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(email.status)}</TableCell>
                    <TableCell className="text-sm text-genie-text-secondary">
                      {new Date(email.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(email)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {canResendEmailLog(email) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resendMutation.mutate(email.id)}
                            disabled={resendMutation.isPending}
                          >
                            <RefreshCw
                              className={`h-4 w-4 ${resendMutation.isPending ? "animate-spin" : ""}`}
                            />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {emailData && emailData.count > 50 && (
            <div className="flex items-center justify-between p-4 border-t border-genie-border">
              <div className="text-sm text-genie-text-secondary">
                Showing {(page - 1) * 50 + 1} to{" "}
                {Math.min(page * 50, emailData.count)} of {emailData.count}{" "}
                emails
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page * 50 >= emailData.count}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Detail Drawer */}
        <SideDrawer
          isOpen={isDetailOpen}
          onClose={() => setIsDetailOpen(false)}
          width="min(1400px, 85vw)"
          title="Email Details"
          branding={{ primaryColor: "#4E9CFF" }}
          actionButton={
            selectedEmail && (
              <div className="flex gap-2">
                {canResendEmailLog(selectedEmail) && (
                  <Button
                    onClick={() => resendMutation.mutate(selectedEmail.id)}
                    disabled={resendMutation.isPending}
                    size="sm"
                  >
                    <RefreshCw
                      className={cn(
                        "h-4 w-4 mr-2",
                        resendMutation.isPending && "animate-spin",
                      )}
                    />
                    Resend
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (!selectedEmail) return;
                    const blob = new Blob([selectedEmail.html_content || ""], {
                      type: "text/html",
                    });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `email-${selectedEmail.id}.html`;
                    a.click();
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download HTML
                </Button>
              </div>
            )
          }
        >
          {selectedEmail && (
            <div className="py-6 space-y-6">
              {/* Overview */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-genie-text-primary">
                  Overview
                </h3>
                <div className="bg-genie-background-muted rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-sm text-genie-text-secondary flex-shrink-0">
                      Type:
                    </span>
                    <Badge
                      className={getEmailTypeColor(selectedEmail.email_type)}
                    >
                      {selectedEmail.email_type}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-sm text-genie-text-secondary flex-shrink-0">
                      Status:
                    </span>
                    {getStatusBadge(selectedEmail.status)}
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-sm text-genie-text-secondary flex-shrink-0">
                      Created:
                    </span>
                    <span className="text-sm text-genie-text-primary text-right">
                      {new Date(selectedEmail.created_at).toLocaleString()}
                    </span>
                  </div>
                  {selectedEmail.sent_at && (
                    <div className="flex justify-between gap-4">
                      <span className="text-sm text-genie-text-secondary flex-shrink-0">
                        Sent:
                      </span>
                      <span className="text-sm text-genie-text-primary text-right">
                        {new Date(selectedEmail.sent_at).toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between gap-4">
                    <span className="text-sm text-genie-text-secondary flex-shrink-0">
                      Provider:
                    </span>
                    <span className="text-sm text-genie-text-primary">
                      {selectedEmail.delivery_provider}
                    </span>
                  </div>
                  {selectedEmail.provider_message_id && (
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
                      <span className="text-sm text-genie-text-secondary flex-shrink-0">
                        Message ID:
                      </span>
                      <span className="text-xs font-mono text-genie-text-primary break-all">
                        {selectedEmail.provider_message_id}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Recipients */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-genie-text-primary">
                  Recipients ({selectedEmail.recipients.length})
                </h3>
                <div className="bg-genie-background-muted rounded-lg p-4">
                  {selectedEmail.recipients.map((recipient, index) => (
                    <div
                      key={index}
                      className="text-sm py-1 text-genie-text-primary break-words"
                    >
                      • <CopyableEmail email={recipient} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Error Details */}
              {selectedEmail.error_message && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-genie-error">
                    Error Details
                  </h3>
                  <div className="bg-genie-error-light rounded-lg p-4 border border-genie-error/20">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-genie-error flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-genie-error break-words">
                          {selectedEmail.error_message}
                        </p>
                        {selectedEmail.error_code && (
                          <p className="text-xs text-genie-error mt-1">
                            Code: {selectedEmail.error_code}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Email Content */}
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                  <h3 className="text-lg font-semibold text-genie-text-primary">
                    Email Content
                  </h3>
                  <div className="flex gap-2">
                    <Button
                      variant={viewMode === "preview" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setViewMode("preview")}
                    >
                      Preview
                    </Button>
                    <Button
                      variant={viewMode === "html" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setViewMode("html")}
                    >
                      HTML
                    </Button>
                    {selectedEmail.text_content && (
                      <Button
                        variant={viewMode === "text" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setViewMode("text")}
                      >
                        Text
                      </Button>
                    )}
                  </div>
                </div>
                <div className="bg-genie-background-muted rounded-lg p-4 border border-genie-border">
                  <p className="text-sm font-semibold mb-4 text-genie-text-primary break-words">
                    Subject: {selectedEmail.subject}
                  </p>
                  <div className="bg-white rounded-lg p-4 md:p-6 border border-genie-border overflow-hidden">
                    {viewMode === "preview" && selectedEmail.html_content && (
                      <div
                        className="prose prose-sm max-w-none overflow-x-auto"
                        dangerouslySetInnerHTML={{
                          __html: selectedEmail.html_content,
                        }}
                      />
                    )}
                    {viewMode === "html" && (
                      <pre className="text-xs whitespace-pre-wrap break-words text-genie-text-primary overflow-x-auto">
                        {selectedEmail.html_content}
                      </pre>
                    )}
                    {viewMode === "text" && (
                      <pre className="text-sm whitespace-pre-wrap break-words text-genie-text-primary overflow-x-auto">
                        {selectedEmail.text_content}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </SideDrawer>
      </div>
    </div>
  );
};

export default EmailLogsPage;
